import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { Filters } from '../../components/uiChrome.jsx';
import { getAuditFacets } from '../../../../services';
import { validateApiResponse } from '../../../../shared/utils/apiResponse';
import { AUDIT_SEVERITY_ORDER, auditSeverityLabelKey } from '../../constants';
import GlobalAuditFiltersDrawer from './GlobalAuditFiltersDrawer.jsx';

const DATE_PRESETS = ['today', '7d', '30d', '90d', 'all'];
const EMPTY_FACETS = { providers: [], instances: [], actors: [], codes: [] };

/**
 * Global Audit filters (CT-4211): the shared {@link Filters} toolbar (search +
 * quick date / severity / provider / instance dropdowns) plus an "All filters"
 * {@link GlobalAuditFiltersDrawer} holding the full set (adds actor + event-code).
 * Both read and write the SAME `useGlobalAudit` state, so a selection made in the
 * bar shows up marked in the slideout and vice versa.
 */
const GlobalAuditFilters = ({
  filters,
  setSearch,
  setDateRange,
  setSeverities,
  setProvider,
  setInstance,
  applyFilters,
  clearFilters,
}) => {
  const { t } = useTranslation('admin-studio');
  const [facets, setFacets] = useState(EMPTY_FACETS);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getAuditFacets().then((res) => {
      if (!cancelled) setFacets(validateApiResponse(res).body ?? EMPTY_FACETS);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const searchProps = useMemo(
    () => ({
      value: filters.search,
      onChange: (e) => setSearch(e?.target ? e.target.value : e),
      onClear: () => setSearch(''),
      placeholder: t('integrations.globalAudit.searchPlaceholder'),
    }),
    [filters.search, setSearch, t],
  );

  // Bar slots (≤4, immediate). Actor / event-code stay in the "All filters" drawer.
  const barFilters = useMemo(() => {
    const single = (value) => (value ? [value] : []);
    const list = [
      {
        id: 'time',
        label: filters.dateRange[0]
          ? t(`integrations.globalAudit.range.${filters.dateRange[0]}`)
          : t('integrations.audit.timeFilter'),
        multiple: false,
        value: filters.dateRange,
        onChange: (next) => setDateRange(next),
        options: DATE_PRESETS.map((p) => ({ value: p, label: t(`integrations.globalAudit.range.${p}`) })),
      },
      {
        id: 'severity',
        label: t('integrations.audit.severityFilter'),
        multiple: true,
        value: filters.severities,
        onChange: setSeverities,
        options: AUDIT_SEVERITY_ORDER.map((s) => ({ value: s, label: t(auditSeverityLabelKey(s)) })),
      },
      {
        id: 'provider',
        label: t('integrations.globalAudit.providerFilter'),
        multiple: false,
        value: single(filters.provider),
        onChange: (next) => setProvider(next[0] ?? ''),
        options: facets.providers.map((p) => ({ value: p.slug, label: p.name })),
      },
    ];
    if (filters.provider) {
      list.push({
        id: 'instance',
        label: t('integrations.globalAudit.instanceFilter'),
        multiple: false,
        value: single(filters.instance),
        onChange: (next) => setInstance(next[0] ?? ''),
        options: facets.instances
          .filter((i) => i.provider_slug === filters.provider)
          .map((i) => ({ value: i.id, label: i.name })),
      });
    }
    return list;
  }, [filters, facets, setDateRange, setSeverities, setProvider, setInstance, t]);

  const i18n = useMemo(() => ({ clearFilters: t('integrations.globalAudit.clearAll') }), [t]);

  return (
    <>
      <Filters
        searchProps={searchProps}
        filters={barFilters}
        maxFilters={4}
        onClearAll={clearFilters}
        onAllFiltersClick={() => setDrawerOpen(true)}
        i18n={i18n}
      />
      <GlobalAuditFiltersDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        filters={filters}
        facets={facets}
        applyFilters={applyFilters}
      />
    </>
  );
};

export default GlobalAuditFilters;
